import React, { useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { Coins } from "lucide-react";
import { useWalletUser, useApp, ConnectPrompt } from "@/App";
import { blackjackAction } from "@/api";

const SUIT = ["♠", "♥", "♦", "♣"];
const FACE = { 1: "A", 11: "J", 12: "Q", 13: "K" };

function cardChar(n, idx) {
  const label = FACE[n] || String(n);
  const suit = SUIT[(idx + n) % 4];
  const red = suit === "♥" || suit === "♦";
  return { label, suit, red };
}

function CardFace({ n, idx, hidden }) {
  if (hidden) return <div className="pcard back">?</div>;
  const { label, suit, red } = cardChar(n, idx);
  return (
    <motion.div initial={{ rotateY: 90, y: -20 }} animate={{ rotateY: 0, y: 0 }} className={`pcard ${red ? "red" : ""}`}>
      <div className="text-center leading-none">
        <div>{label}</div>
        <div className="text-base">{suit}</div>
      </div>
    </motion.div>
  );
}

export default function BlackjackPage() {
  const { isConnected, user, address, refresh } = useWalletUser();
  const { showToast } = useApp();
  const [wager, setWager] = useState(50);
  const [session, setSession] = useState(null);
  const [busy, setBusy] = useState(false);

  if (!isConnected) return <ConnectPrompt />;

  const act = async (action) => {
    setBusy(true);
    try {
      const r = await blackjackAction({ address, wager: Number(wager), action, session_id: session?.session_id });
      setSession({ ...session, ...r });
      if (["win", "lose", "push", "bust", "blackjack"].includes(r.status)) {
        const msg = r.status === "win" ? `You won +${r.flame_won} FLAME` :
                    r.status === "blackjack" ? `BLACKJACK! +${r.flame_won} FLAME` :
                    r.status === "push" ? `Push — ${r.flame_won} FLAME refunded` :
                    r.status === "bust" ? `Bust! You went over 21.` : `Dealer wins`;
        showToast(msg, ["lose", "bust"].includes(r.status) ? "error" : "info");
        refresh();
      }
    } catch (e) { showToast(e?.response?.data?.detail || "Error", "error"); }
    finally { setBusy(false); }
  };

  const playing = session?.status === "playing";
  const finished = session && !playing;

  return (
    <div className="pt-24 pb-20">
      <div className="max-w-5xl mx-auto px-4">
        <h1 className="font-display text-4xl font-black">Cronos Blackjack</h1>
        <p className="text-slate-400 mt-1">Credits: <span className="text-cyan-400 font-mono">{user?.credits ?? 0}</span> • FLAME: <span className="text-orange-400 font-mono">{user?.flame ?? 0}</span></p>

        <div className="mt-6 card-tile card-flame" data-testid="game-blackjack-table">
          <div className="rounded-2xl bg-gradient-to-br from-emerald-900/40 to-emerald-700/20 border border-emerald-500/30 p-6">
            <div className="text-xs font-pixel text-emerald-300">DEALER{session?.dealer_total ? ` — ${session.dealer_total}` : ""}</div>
            <div className="flex gap-2 mt-2 min-h-[110px]">
              {session ? (session.dealer ? session.dealer : session.dealer_shown || []).map((c, i) => (
                <CardFace key={`d-${i}`} n={c} idx={i} />
              )) : null}
              {session?.dealer_shown && !session.dealer && <CardFace hidden />}
              {!session && <div className="text-slate-500 italic">Place a wager and deal.</div>}
            </div>
            <div className="h-6" />
            <div className="text-xs font-pixel text-orange-300">YOU{session?.player_total ? ` — ${session.player_total}` : ""}</div>
            <div className="flex gap-2 mt-2 min-h-[110px]">
              {session?.player?.map((c, i) => <CardFace key={`p-${i}`} n={c} idx={i} />)}
            </div>
          </div>

          <div className="mt-5 flex items-center gap-3 flex-wrap">
            <div className="flex items-center gap-2">
              <Coins size={16} className="text-cyan-400" />
              <input data-testid="blackjack-wager-input" type="number" min="10" step="10" value={wager}
                onChange={(e) => setWager(Number(e.target.value))} disabled={playing}
                className="bg-[#12131C] border border-[#2A2E45] rounded-xl px-4 py-2 w-32 font-mono disabled:opacity-50" />
            </div>
            {(!session || finished) && (
              <button data-testid="game-blackjack-deal-button" disabled={busy || !user || user.credits < wager}
                onClick={() => { setSession(null); act("deal"); }} className="btn-tactile btn-flame">Deal</button>
            )}
            {playing && (
              <>
                <button data-testid="game-blackjack-hit-button" disabled={busy} onClick={() => act("hit")} className="btn-tactile btn-cronos">Hit</button>
                <button data-testid="game-blackjack-stand-button" disabled={busy} onClick={() => act("stand")} className="btn-tactile btn-gold">Stand</button>
              </>
            )}
            {finished && (
              <div className="chip border-orange-500/40">{String(session.status).toUpperCase()} {session.flame_won ? `• +${session.flame_won} FLAME` : ""}</div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}
